import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Users, Phone, MapPin, Star, Sparkles, ExternalLink, Mail, MessageSquare, Ban } from 'lucide-react';
import { doc, onSnapshot } from 'firebase/firestore';
import { useLeads } from '@/hooks/useFirestore';
import { useAuth } from '@/hooks/useAuth';
import { cloudFunctions, db, logAnalyticsEvent } from '@/lib/firebase';
import { SiteCreationModal } from '@/components/site/SiteCreationModal';
import type { LayoutType } from '@/lib/templates';
import { HackerLoadingScreen } from '@/components/ui/HackerLoadingScreen';
import { QuotaLimitModal } from '@/components/ui/QuotaLimitModal';
import type { Lead } from '@shared/types';

export function LeadsPage() {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [search, setSearch] = useState('');
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [reportingId, setReportingId] = useState<string | null>(null);

  // Google's listing sometimes omits a business's website. Reporting it
  // excludes the lead and gives the lead credit back.
  const handleReportHasWebsite = async (lead: Lead & { id: string }) => {
    setReportingId(lead.id);
    try {
      const result = await cloudFunctions.reportLeadHasWebsite({ leadId: lead.id });
      logAnalyticsEvent('lead_reported_has_website', { refunded: result.refunded });
      toast.success(result.refunded
        ? `${lead.businessName} marked as having a website. Your lead credit was returned.`
        : `${lead.businessName} marked as having a website.`);
    } catch (error) {
      toast.error(error instanceof Error && error.message ? error.message : 'Could not report this lead');
    } finally {
      setReportingId(null);
    }
  };
  const [selectedLead, setSelectedLead] = useState<(Lead & { id: string }) | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showHackerScreen, setShowHackerScreen] = useState(false);
  const [generationLogs, setGenerationLogs] = useState<string[]>([]);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const progressUnsubRef = useRef<(() => void) | null>(null);

  // Clean up Firestore listener on unmount
  useEffect(() => {
    return () => { progressUnsubRef.current?.(); };
  }, []);

  const { data: leads, isLoading, error, refetch } = useLeads();

  // Filter out leads that already have sites generated, and apply search filter
  const filteredLeads = leads?.filter((lead) =>
    !lead.siteGenerated && lead.businessName.toLowerCase().includes(search.toLowerCase())
  ) || [];

  const openSiteCreationModal = (lead: Lead & { id: string }) => {
    // Check if user has reached sites quota
    const sitesUsed = userData?.usage?.sitesUsed || 0;
    const sitesLimit = userData?.quotas?.sitesLimit || 10;
    if (sitesUsed >= sitesLimit) {
      setShowQuotaModal(true);
      return;
    }

    setSelectedLead(lead);
    setShowModal(true);
  };

  const handleGenerateSite = async (templateId: string, selectedFeatures: string[], selectedLayouts: LayoutType[]) => {
    if (!selectedLead) return;
    setGeneratingId(selectedLead.id);
    setShowModal(false);
    setShowHackerScreen(true);
    setGenerationLogs([]);

    // Show actual business data being processed
    const businessInfo = [
      `✓ Business: ${selectedLead.businessName}`,
      selectedLead.location ? `✓ Location: ${selectedLead.location}` : null,
      selectedLead.rating ? `✓ Rating: ${selectedLead.rating} stars (${selectedLead.reviewCount || 0} reviews)` : null,
      selectedLead.niche ? `✓ Category: ${selectedLead.niche}` : null,
      `✓ Template: ${templateId}`,
      selectedFeatures.length > 0 ? `✓ Features: ${selectedFeatures.join(', ')}` : null,
      selectedLayouts.length > 0 ? `✓ Layouts: ${selectedLayouts.join(', ')}` : null,
    ].filter(Boolean) as string[];

    // Show business info immediately
    setGenerationLogs(businessInfo);

    // Subscribe to real-time progress updates from the backend
    const seenStages = new Set<string>();
    progressUnsubRef.current?.(); // Clean up any previous listener
    const progressDocRef = doc(db, 'generationProgress', selectedLead.id);
    progressUnsubRef.current = onSnapshot(progressDocRef, (snap) => {
      const data = snap.data();
      if (!data) return;
      const { stage, sectionsFound } = data as { stage?: string; sectionsFound?: string[]; status?: string };

      // Add new stage as a log entry
      if (stage && !seenStages.has(stage)) {
        seenStages.add(stage);
        const prefix = stage.includes('ready') || stage.includes('Saving') ? '✓' : '⟳';
        setGenerationLogs(prev => [...prev, `${prefix} ${stage}`]);
      }

      // Log newly discovered sections
      if (sectionsFound && sectionsFound.length > 0) {
        sectionsFound.forEach((section: string) => {
          const key = `section:${section}`;
          if (!seenStages.has(key)) {
            seenStages.add(key);
            const friendlyName = section.replace(/-/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
            setGenerationLogs(prev => [...prev, `✓ Built ${friendlyName}`]);
          }
        });
      }
    });

    try {
      const result = await cloudFunctions.generateSiteContent({
        leadId: selectedLead.id,
        templateType: templateId,
        selectedFeatures,
        selectedLayouts,
      });

      // Unsubscribe from progress
      progressUnsubRef.current?.();
      progressUnsubRef.current = null;

      // Add completion logs
      setGenerationLogs(prev => [
        ...prev,
        `✓ Website generated successfully`,
        `✓ Opening site editor...`,
      ]);

      // Wait to show final logs
      await new Promise(resolve => setTimeout(resolve, 1500));

      setShowHackerScreen(false);
      logAnalyticsEvent('site_generated', {
        site_id: result.siteId,
        template: templateId,
      });
      toast.success(`Site created for ${selectedLead.businessName}!`);
      setSelectedLead(null);
      refetch();
      navigate(`/dashboard/sites/${result.siteId}/edit`);
    } catch (err: unknown) {
      progressUnsubRef.current?.();
      progressUnsubRef.current = null;
      setGenerationLogs(prev => [...prev, `✗ Error: ${err instanceof Error ? err.message : 'Unknown error'}`]);
      await new Promise(resolve => setTimeout(resolve, 2000));
      setShowHackerScreen(false);
      const message = err instanceof Error ? err.message : 'Failed to generate site';
      toast.error(message);
    } finally {
      setGeneratingId(null);
    }
  };

  const cardStyle: React.CSSProperties = {
    background: 'rgba(15, 15, 15, 0.95)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px',
    padding: '20px',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  };

  const primaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    background: 'linear-gradient(to right, #B08080, #B08080)',
    color: '#000000',
  };

  const outlineButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.2)',
    color: '#FFFBFE',
  };

  const badgeStyle = (variant: 'success' | 'warning'): React.CSSProperties => ({
    padding: '4px 10px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: 500,
    background: variant === 'success' ? 'rgba(91,58,58,0.2)' : 'rgba(136,136,136,0.2)',
    color: variant === 'success' ? '#B08080' : '#888888',
  });

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-snow uppercase tracking-widest">Leads</h1>
          <p className="text-gray-400 mt-1">Error loading leads</p>
        </div>
        <div style={{ ...cardStyle, textAlign: 'center', padding: '48px' }}>
          <p style={{ color: '#B08080' }}>{error instanceof Error ? error.message : 'Unknown error'}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-snow uppercase tracking-widest">Leads</h1>
          <p className="text-gray-400 mt-1">Loading...</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {[1, 2, 3].map((i) => (
            <div key={i} style={{ ...cardStyle, opacity: 0.5 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '48px', height: '48px', background: 'rgba(255,255,255,0.1)', borderRadius: '12px' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ height: '20px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', width: '25%', marginBottom: '8px' }} />
                  <div style={{ height: '16px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', width: '33%' }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-snow uppercase tracking-widest">Leads</h1>
        <p className="text-gray-400 mt-1">{leads?.length || 0} total leads</p>
      </div>

      <div style={{ ...cardStyle, marginBottom: '24px' }}>
        <input
          type="text"
          placeholder="Search leads..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: '100%',
            maxWidth: '400px',
            padding: '12px 16px',
            borderRadius: '8px',
            border: '1px solid rgba(255,255,255,0.1)',
            background: 'rgba(255,255,255,0.05)',
            color: '#FFFBFE',
            fontSize: '14px',
            outline: 'none',
          }}
        />
      </div>

      {filteredLeads.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {filteredLeads.map((lead) => (
            <div key={lead.id} className="hover-lift animate-list-in" style={cardStyle}>
              <div style={{ display: 'flex', gap: '16px' }}>
                {/* Main content */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flex: 1 }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, rgba(91,58,58,0.2), rgba(91,58,58,0.2))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#B08080',
                      flexShrink: 0,
                    }}>
                      <Users style={{ width: '24px', height: '24px' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFBFE' }}>{lead.businessName}</h3>
                      {lead.rating && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: '#888888' }}>
                          <Star style={{ width: '16px', height: '16px', fill: 'currentColor' }} />
                          {lead.rating}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '4px', fontSize: '14px', color: '#888888', flexWrap: 'wrap' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <MapPin style={{ width: '16px', height: '16px' }} />
                        {lead.location}
                      </span>
                      <span>{lead.niche}</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginTop: '8px', alignItems: 'center' }}>
                      {/* Contact method badge */}
                      {lead.contactMethod === 'email' || (lead.email && !lead.contactMethod) ? (
                        <span style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          background: 'rgba(91,58,58,0.2)',
                          color: '#B08080',
                        }}>
                          <Mail style={{ width: '12px', height: '12px' }} />
                          Email
                        </span>
                      ) : lead.contactMethod === 'sms' || lead.smsCapable ? (
                        <span style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          background: 'rgba(136,136,136,0.2)',
                          color: '#888888',
                        }}>
                          <MessageSquare style={{ width: '12px', height: '12px' }} />
                          SMS
                        </span>
                      ) : null}
                      {lead.email && (
                        <a href={`mailto:${lead.email}`} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: '#B08080', textDecoration: 'none' }}>
                          <Mail style={{ width: '16px', height: '16px' }} />
                          {lead.email}
                        </a>
                      )}
                      {lead.phone && (
                        <a href={`tel:${lead.phone}`} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: '#888888', textDecoration: 'none' }}>
                          <Phone style={{ width: '16px', height: '16px' }} />
                          {lead.phone}
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    {lead.hasWebsite && (
                      <span style={badgeStyle('warning')}>
                        Has Website
                      </span>
                    )}
                    {lead.siteGenerated ? (
                      <Link to="/dashboard/sites" style={{ ...outlineButtonStyle, textDecoration: 'none' }}>
                        <ExternalLink style={{ width: '16px', height: '16px' }} />
                        View Site
                      </Link>
                    ) : (
                      <>
                      {!lead.hasWebsite && (
                        <button
                          onClick={() => handleReportHasWebsite(lead)}
                          disabled={reportingId === lead.id}
                          title="This business already has a website - remove it and get the lead credit back"
                          style={{ ...outlineButtonStyle, opacity: reportingId === lead.id ? 0.5 : 1 }}
                        >
                          <Ban style={{ width: '16px', height: '16px' }} />
                          {reportingId === lead.id ? 'Reporting...' : 'Has a website'}
                        </button>
                      )}
                      <button
                        onClick={() => openSiteCreationModal(lead)}
                        disabled={lead.hasWebsite || generatingId === lead.id}
                        style={{
                          ...primaryButtonStyle,
                          opacity: lead.hasWebsite || generatingId === lead.id ? 0.5 : 1,
                          cursor: lead.hasWebsite || generatingId === lead.id ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <Sparkles style={{ width: '16px', height: '16px' }} />
                        {generatingId === lead.id ? 'Generating...' : 'Generate Site'}
                      </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Google Places Image */}
                {lead.businessImages && lead.businessImages[0] && (
                  <div style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    flexShrink: 0,
                    background: 'linear-gradient(135deg, rgba(91,58,58,0.1), rgba(91,58,58,0.05))',
                  }}>
                    <img
                      src={lead.businessImages[0]}
                      alt={lead.businessName}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ ...cardStyle, textAlign: 'center', padding: '48px' }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: 'rgba(91,58,58,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <Users style={{ width: '32px', height: '32px', color: '#B08080' }} />
          </div>
          <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFBFE', marginBottom: '8px' }}>No Leads</h3>
          <p style={{ color: '#888888', marginBottom: '24px' }}>
            Go to Campaigns, create a campaign, and click "Scrape Leads" to find businesses
          </p>
          <Link to="/dashboard/campaigns" style={{ ...primaryButtonStyle, textDecoration: 'none' }}>
            Go to Campaigns
          </Link>
        </div>
      )}

      <SiteCreationModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedLead(null);
        }}
        onSubmit={handleGenerateSite}
        businessName={selectedLead?.businessName || ''}
        loading={generatingId !== null}
      />

      {/* Hacker-style loading screen for site generation */}
      <HackerLoadingScreen
        isOpen={showHackerScreen}
        title="GENERATING WEBSITE"
        subtitle={`Creating custom site for ${selectedLead?.businessName || 'business'}`}
        mode="generating"
        logs={generationLogs}
      />

      {/* Quota limit modal */}
      <QuotaLimitModal
        isOpen={showQuotaModal}
        onClose={() => setShowQuotaModal(false)}
        type="sites"
        currentUsage={userData?.usage?.sitesUsed || 0}
        limit={userData?.quotas?.sitesLimit || 10}
        planId={userData?.planId}
      />
    </div>
  );
}
